import type { JSX } from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import type { MenuProps } from 'antd';
import { Dropdown, Empty, Input, Modal, Popconfirm, Skeleton, message } from 'antd';
import {
  ApartmentOutlined,
  ClockCircleOutlined,
  DeleteOutlined,
  EditOutlined,
  ExclamationCircleOutlined,
  FileTextOutlined,
  FireOutlined,
  HolderOutlined,
  LoadingOutlined,
  MoreOutlined,
  PaperClipOutlined,
  PlusOutlined,
  ReadOutlined,
  SafetyCertificateOutlined,
  SearchOutlined,
  ThunderboltOutlined,
} from '@ant-design/icons';

import { authApi } from '../lib/authApi';
import {
  editorApi,
  wikiAdminApi,
  wikiApi,
  type GroupedSearchDto,
  type PopularArticleDto,
  type WikiArticleListItem,
  type WikiBlockDto,
} from '../lib/wikiApi';
import { kbApi, kbShortDate, type KbBlock } from '../lib/api/kb';
import { NewArticleModal } from '../components/NewArticleModal';
import {
  ICON_CHOICES,
  MATERIAL_LABELS,
  SECTION_ICONS,
  TYPE_CHIPS,
  materialIcon,
  renderSectionIcon,
  sectionAccent,
  sectionSoft,
} from '../lib/sectionIcons';
import { Button, Chip, PageTitle, fmtInt, plural } from '../components/ds';
import './KnowledgeBase.css';

/**
 * База знаний — хаб (HUB 2.0, прототип wk6/PageKnowledge «kb»):
 *   1. Заголовок «База знаний» + «N статей в M разделах · X % актуальны»;
 *      УК — «Граф знаний» и «Новая статья».
 *   2. Тёмный герой «Что вы ищете?»: поиск с выпадашкой (grouped-search, как
 *      Cmd+K), «Найти» → /search, «Спросить AI» → /ai, «Часто ищут:» — реальные
 *      запросы из журнала поиска (GET /wiki/search/popular).
 *   3. Чипы типов: выбранный тип показывает список таких статей (listArticles
 *      с серверным фильтром type) и приглушает разделы без них.
 *   4. Сетка разделов (иконка, цвет, число статей, описание) — у админа ⋮-меню
 *      и drag-перестановка за ручку.
 *   5. Рейлы: Популярные · Недавно обновлено · УК — «Ждут вашего решения»
 *      (на согласовании + просроченная проверка актуальности), остальным —
 *      «Рекомендовано» (персональная подборка бэка).
 * Данные только реальные (правило «без mock»).
 */

// Описания разделов — фолбэк, пока у раздела не заполнено wiki_blocks.description.
const SPACE_DESCRIPTIONS: Record<string, string> = {
  opening: 'Запуск ресторана: от помещения до первого заказа',
  operational: 'Ежедневные операции, смены, чек-листы',
  management: 'Команда, найм, рост выручки',
  marketing: 'Акции, агрегаторы, локальное продвижение',
  kitchen: 'Технологии, рецептуры, хранение продуктов',
  callcenter: 'Скрипты, обработка заказов, возражения',
  finance: 'Роялти, отчётность, ценообразование',
  admin: 'BRASOL CRM, iiko, интеграции, оборудование',
  'crm-it': 'BRASOL CRM, iiko, интеграции, оборудование',
  faq: 'Частые вопросы партнёров и быстрые ответы',
};

// Палитра цветов раздела (бренд + мягкие акценты).
const COLOR_CHOICES: string[] = ['#251D4A', '#FF6900', '#0096FF', '#16A34A', '#7C3AED', '#E8590C', '#0E9F6E', '#64748B'];

const softColor = sectionSoft;

// ── Инлайн-поиск в герое БЗ ───────────────────────────────────────────
type SearchFlatItem =
  | { kind: 'article' | 'standard'; key: string; title: string; sub: string | null; slug: string }
  | { kind: 'file'; key: string; title: string; sub: string | null; articleSlug: string | null }
  | { kind: 'course'; key: string; title: string; sub: string | null; id: string };

function searchKindIcon(kind: SearchFlatItem['kind']): JSX.Element {
  switch (kind) {
    case 'standard':
      return <SafetyCertificateOutlined />;
    case 'file':
      return <PaperClipOutlined />;
    case 'course':
      return <ReadOutlined />;
    default:
      return <FileTextOutlined />;
  }
}

function buildSearchFlat(data: GroupedSearchDto | undefined): SearchFlatItem[] {
  if (!data) return [];
  const flat: SearchFlatItem[] = [];
  for (const a of data.articles)
    flat.push({ kind: 'article', key: `a:${a.id}`, title: a.title, sub: a.snippet ?? a.categoryName ?? null, slug: a.slug });
  for (const s of data.standards)
    flat.push({ kind: 'standard', key: `s:${s.id}`, title: s.title, sub: s.snippet ?? s.categoryName ?? null, slug: s.slug });
  for (const f of data.files)
    flat.push({ kind: 'file', key: `f:${f.id}`, title: f.fileName, sub: f.articleTitle, articleSlug: f.articleSlug });
  for (const c of data.courses)
    flat.push({ kind: 'course', key: `c:${c.id}`, title: c.name, sub: c.description, id: c.id });
  return flat;
}

/** Подпись раздела статьи: категория → имя первого блока. */
function sectionOf(a: { categoryName: string | null; blocks: string[] }, blockNameByCode: Record<string, string>): string {
  return a.categoryName || (a.blocks[0] ? blockNameByCode[a.blocks[0]] ?? '' : '');
}

/** Элемент рейла (единый вид «заголовок + серая подпись»). */
interface RailItem {
  key: string;
  to: string;
  title: string;
  meta: string;
}

function popularMeta(a: PopularArticleDto, blockNameByCode: Record<string, string>): string {
  const section = a.categoryName || (a.blockCode ? blockNameByCode[a.blockCode] : '') || '';
  return [section, `${fmtInt(a.viewCount)} ${plural(a.viewCount, 'просмотр', 'просмотра', 'просмотров')}`].filter(Boolean).join(' · ');
}

export function KnowledgeBasePage(): JSX.Element {
  const [activeType, setActiveType] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [newArticleOpen, setNewArticleOpen] = useState(false);
  // «Добавить статью» из меню раздела — та же модалка, раздел уже выбран (16.10.2026: вела на
  // несуществующий /article/new — «Статья «new» не найдена»).
  const [newArticleBlock, setNewArticleBlock] = useState<string | undefined>(undefined);
  const [editingBlock, setEditingBlock] = useState<WikiBlockDto | null>(null);
  const qc = useQueryClient();
  const navigate = useNavigate();

  // ── Инлайн-поиск: строка героя + выпадашка результатов ──
  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [searchOpen, setSearchOpen] = useState(false);
  const searchBoxRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedQuery(query.trim()), 250);
    return () => clearTimeout(t);
  }, [query]);

  const searchQuery = useQuery({
    queryKey: ['wiki', 'grouped', debouncedQuery],
    queryFn: () => wikiApi.searchGrouped(debouncedQuery),
    enabled: searchOpen && debouncedQuery.length >= 2,
    staleTime: 30_000,
  });
  const searchFlat = useMemo(() => buildSearchFlat(searchQuery.data), [searchQuery.data]);
  const searchGroups = useMemo(() => {
    const groups: Array<{ label: string; items: SearchFlatItem[] }> = [];
    const by = (k: SearchFlatItem['kind']) => searchFlat.filter((f) => f.kind === k);
    const arts = by('article');
    const stds = by('standard');
    const files = by('file');
    const courses = by('course');
    if (arts.length) groups.push({ label: 'Статьи', items: arts });
    if (stds.length) groups.push({ label: 'Стандарты', items: stds });
    if (files.length) groups.push({ label: 'Файлы', items: files });
    if (courses.length) groups.push({ label: 'Курсы', items: courses });
    return groups;
  }, [searchFlat]);

  useEffect(() => {
    if (!searchOpen) return;
    const onDocClick = (e: MouseEvent) => {
      if (searchBoxRef.current && !searchBoxRef.current.contains(e.target as Node)) setSearchOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setSearchOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [searchOpen]);

  const goSearchItem = (it: SearchFlatItem): void => {
    setSearchOpen(false);
    if (it.kind === 'article' || it.kind === 'standard') navigate(`/article/${encodeURIComponent(it.slug)}`);
    else if (it.kind === 'course') navigate(`/course/${it.id}`);
    else if (it.kind === 'file') navigate(it.articleSlug ? `/article/${encodeURIComponent(it.articleSlug)}` : '/disk');
  };
  const doSearch = (): void => {
    const q = query.trim();
    if (q.length >= 2) navigate(`/search?q=${encodeURIComponent(q)}`);
  };
  const askAi = (): void => {
    const q = query.trim();
    navigate(q ? `/ai?q=${encodeURIComponent(q)}` : '/ai');
  };

  const meQuery = useQuery({ queryKey: ['wiki-auth', 'me'], queryFn: () => authApi.me(), retry: false, staleTime: 60_000 });
  const blocksQuery = useQuery({
    queryKey: ['wiki', 'blocks'],
    queryFn: () => wikiApi.listBlocks() as Promise<KbBlock[]>,
    staleTime: 5 * 60_000,
  });
  // Пул опубликованных статей (publishedAt desc) — рейл «Недавно обновлено».
  const poolQuery = useQuery({
    queryKey: ['wiki', 'articles', 'kb-pool'],
    queryFn: () => wikiApi.listArticles({ status: 'PUBLISHED', limit: 60 }),
    retry: false,
    staleTime: 5 * 60_000,
  });
  const popularQuery = useQuery({
    queryKey: ['wiki', 'articles', 'popular'],
    queryFn: () => wikiApi.popularArticles(5),
    retry: false,
    staleTime: 5 * 60_000,
  });
  // «Часто ищут:» — реальные запросы (≥2 разных пользователей за 30 дней).
  const popularQ = useQuery({
    queryKey: ['wiki', 'search', 'popular'],
    queryFn: () => kbApi.popularQueries(6),
    retry: false,
    staleTime: 10 * 60_000,
  });
  // Выбранный тип — серверный фильтр listArticles({type}).
  const typeQuery = useQuery({
    queryKey: ['wiki', 'articles', 'kb-type', activeType],
    queryFn: () => wikiApi.listArticles({ status: 'PUBLISHED', type: activeType ?? undefined, limit: 60 }),
    enabled: !!activeType,
    staleTime: 5 * 60_000,
  });

  const canCreate = meQuery.data?.canCreate ?? false;
  const canAdmin = meQuery.data?.canAdmin ?? false;
  // «Ждут вашего решения» — экран УК (kb:admin), как в прототипе.
  const canReview = canAdmin;
  const blocks = blocksQuery.data ?? [];
  const pool = poolQuery.data?.items ?? [];

  // УК: «Ждут вашего решения» = статьи на согласовании + просроченная проверка.
  const reviewQueue = useQuery({
    queryKey: ['wiki', 'review-queue'],
    queryFn: () => editorApi.listReviewQueue(),
    enabled: canReview,
    retry: false,
    staleTime: 60_000,
  });
  const verifyQueue = useQuery({
    queryKey: ['wiki', 'admin', 'verify-queue'],
    queryFn: () => wikiAdminApi.verifyQueue(),
    enabled: canAdmin,
    retry: false,
    staleTime: 60_000,
  });
  const recommendedQuery = useQuery({
    queryKey: ['wiki', 'articles', 'recommended', 5],
    queryFn: () => wikiApi.recommended(5),
    enabled: !canReview && meQuery.isSuccess,
    retry: false,
    staleTime: 5 * 60_000,
  });

  const createMutation = useMutation({
    mutationFn: (data: { name: string; icon: string; color: string }) => wikiAdminApi.createBlock(data),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['wiki', 'blocks'] });
      message.success('Раздел создан');
      setCreateOpen(false);
    },
    onError: () => message.error('Не удалось создать раздел'),
  });
  const updateMutation = useMutation({
    mutationFn: (data: { id: string; name: string; icon: string; color: string }) =>
      wikiAdminApi.updateBlock(data.id, { name: data.name, icon: data.icon, color: data.color }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['wiki', 'blocks'] });
      message.success('Раздел обновлён');
      setEditingBlock(null);
    },
    onError: () => message.error('Не удалось обновить раздел'),
  });
  const deleteMutation = useMutation({
    mutationFn: (id: string) => wikiAdminApi.deleteBlock(id),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['wiki', 'blocks'] });
      message.success('Раздел удалён');
      setEditingBlock(null);
    },
    onError: (e: unknown) => {
      const msg = e instanceof Error && e.message ? e.message : 'Не удалось удалить раздел';
      message.error(msg);
    },
  });
  const reorderMutation = useMutation({
    mutationFn: (updates: Array<{ id: string; sortOrder: number }>) =>
      Promise.all(updates.map((u) => wikiAdminApi.updateBlock(u.id, { sortOrder: u.sortOrder }))),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['wiki', 'blocks'] });
    },
    onError: async () => {
      message.error('Не удалось сохранить порядок');
      await qc.invalidateQueries({ queryKey: ['wiki', 'blocks'] });
    },
  });

  // Локальный порядок карточек для drag-n-drop (оптимистичный).
  const [orderedBlocks, setOrderedBlocks] = useState<KbBlock[]>(blocks);
  useEffect(() => {
    setOrderedBlocks(blocks);
  }, [blocks]);
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const [overIndex, setOverIndex] = useState<number | null>(null);
  const handleDrop = (from: number, to: number): void => {
    setDragIndex(null);
    setOverIndex(null);
    if (from === to) return;
    const next = [...orderedBlocks];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    setOrderedBlocks(next);
    const updates: Array<{ id: string; sortOrder: number }> = [];
    next.forEach((b, i) => {
      const so = i * 10;
      if (b.sortOrder !== so) updates.push({ id: b.id, sortOrder: so });
    });
    if (updates.length) reorderMutation.mutate(updates);
  };

  const blockNameByCode = useMemo(() => {
    const m: Record<string, string> = {};
    for (const b of blocks) m[b.code] = b.name;
    return m;
  }, [blocks]);

  const typeItems: WikiArticleListItem[] = typeQuery.data?.items ?? [];
  // Разделы, где есть статьи выбранного типа — остальные приглушаем.
  const blocksWithType = useMemo(() => {
    if (!activeType || !typeQuery.data) return null;
    const set = new Set<string>();
    for (const a of typeQuery.data.items) for (const code of a.blocks) set.add(code);
    return set;
  }, [activeType, typeQuery.data]);

  // ── Подзаголовок: статьи, разделы, актуальность (УК) ──
  const totalArticles = blocks.reduce((sum, b) => sum + b.articleCount, 0);
  const overdue = verifyQueue.data?.counts.overdue ?? null;
  const freshPct = canAdmin && overdue != null && totalArticles > 0 ? Math.max(0, Math.round(((totalArticles - overdue) / totalArticles) * 100)) : null;
  const subParts = [
    `${fmtInt(totalArticles)} ${plural(totalArticles, 'статья', 'статьи', 'статей')} в ${blocks.length} ${plural(blocks.length, 'разделе', 'разделах', 'разделах')}`,
    freshPct != null ? `${freshPct} % актуальны` : null,
  ].filter(Boolean);

  // ── Рейлы ──
  const recentRail: RailItem[] = pool.slice(0, 5).map((a) => ({
    key: a.id,
    to: `/article/${a.slug}`,
    title: a.title,
    meta: [sectionOf(a, blockNameByCode), kbShortDate(a.publishedAt)].filter(Boolean).join(' · '),
  }));
  const popularRail: RailItem[] = (popularQuery.data ?? []).length
    ? (popularQuery.data ?? []).map((a) => ({ key: a.id, to: `/article/${a.slug}`, title: a.title, meta: popularMeta(a, blockNameByCode) }))
    : pool.slice(5, 10).map((a) => ({ key: a.id, to: `/article/${a.slug}`, title: a.title, meta: sectionOf(a, blockNameByCode) }));
  const decisionsRail: RailItem[] = [
    ...(reviewQueue.data ?? []).slice(0, 4).map((a) => ({
      key: `r:${a.id}`,
      to: `/article/${a.slug}`,
      title: a.title,
      meta: `На согласовании · ${kbShortDate(a.updatedAt)}`,
    })),
    ...(verifyQueue.data?.overdue ?? []).slice(0, 4).map((a) => ({
      key: `v:${a.id}`,
      to: `/article/${a.slug}`,
      title: a.title,
      meta: `Проверка просрочена${a.ownerName ? ` · ${a.ownerName}` : ''}`,
    })),
  ].slice(0, 5);
  const recommendedRail: RailItem[] = (recommendedQuery.data ?? []).map((a) => ({
    key: a.id,
    to: `/article/${a.slug}`,
    title: a.title,
    meta: a.reason,
  }));

  const rails: Array<{ key: string; icon: JSX.Element; title: string; items: RailItem[]; loading: boolean; empty: string; more?: { to: string; label: string } }> = [
    { key: 'popular', icon: <FireOutlined />, title: 'Популярные', items: popularRail, loading: popularQuery.isLoading || poolQuery.isLoading, empty: 'Пока нет просмотров' },
    { key: 'recent', icon: <ClockCircleOutlined />, title: 'Недавно обновлено', items: recentRail, loading: poolQuery.isLoading, empty: 'Нет статей' },
    canReview
      ? {
          key: 'decide',
          icon: <ExclamationCircleOutlined />,
          title: 'Ждут вашего решения',
          items: decisionsRail,
          loading: reviewQueue.isLoading || (canAdmin && verifyQueue.isLoading),
          empty: 'Всё разобрано — согласований и просрочек нет',
          more: canAdmin ? { to: '/admin/freshness', label: 'Вся очередь актуальности' } : undefined,
        }
      : { key: 'rec', icon: <ThunderboltOutlined />, title: 'Рекомендовано', items: recommendedRail, loading: recommendedQuery.isLoading, empty: 'Подборка появится, когда вы начнёте читать' },
  ];

  return (
    <div className="fk-kb-stack">
      <PageTitle
        title="База знаний"
        sub={subParts.join(' · ')}
        style={{ marginBottom: 0 }}
        right={
          <>
            {canAdmin && (
              <Button variant="ghost" icon={<ApartmentOutlined />} to="/graph">
                Граф знаний
              </Button>
            )}
            {canCreate && (
              <Button variant="primary" icon={<PlusOutlined />} onClick={() => setNewArticleOpen(true)}>
                Новая статья
              </Button>
            )}
          </>
        }
      />
      <NewArticleModal
        open={newArticleOpen}
        onClose={() => {
          setNewArticleOpen(false);
          setNewArticleBlock(undefined);
        }}
        blockCode={newArticleBlock}
      />

      {/* Герой «Что вы ищете?» */}
      <section className="fk-kb-hero">
        <div className="fk-kb-hero-art">
          <img src="/brand/illustrations/sushi-set.png" alt="" />
        </div>
        <div style={{ position: 'relative' }}>
          <h2>Что вы ищете?</h2>
          <div className="fk-kb-hero-sub">инструкцию, стандарт, тех-карту, документ — или спросите AI</div>
          <div ref={searchBoxRef} className="fk-kb-hero-form">
            <div className="fk-kb-hero-input">
              <SearchOutlined />
              <input
                value={query}
                onChange={(e) => {
                  setQuery(e.target.value);
                  setSearchOpen(true);
                }}
                onFocus={() => setSearchOpen(true)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') doSearch();
                }}
                placeholder="Например: закрытие смены, маркировка, роялти…"
                aria-label="Поиск по базе знаний"
              />
              {searchOpen && searchQuery.isFetching && (
                <span style={{ position: 'absolute', right: 14, color: 'var(--fk-text-muted)', display: 'flex' }}>
                  <LoadingOutlined spin />
                </span>
              )}
            </div>
            <Button variant="primary" onClick={doSearch}>
              Найти
            </Button>
            <Button variant="ghost" className="fk-kb-ghost-dark" icon={<ThunderboltOutlined />} onClick={askAi}>
              Спросить AI
            </Button>

            {searchOpen && debouncedQuery.length >= 2 && (
              <div className="fk-kb-dropdown">
                {searchQuery.isLoading ? (
                  <div style={{ padding: 16 }}>
                    <Skeleton active paragraph={{ rows: 3 }} />
                  </div>
                ) : searchFlat.length === 0 ? (
                  <div style={{ padding: '18px 16px', textAlign: 'center', color: 'var(--fk-text-muted)', fontSize: 13.5 }}>
                    Ничего не найдено по «{debouncedQuery}».{' '}
                    <Link to={`/ai?q=${encodeURIComponent(debouncedQuery)}`} style={{ fontWeight: 600 }}>
                      Спросить AI
                    </Link>
                  </div>
                ) : (
                  <>
                    {searchGroups.map((g) => (
                      <div key={g.label}>
                        <div className="fk-kb-cap" style={{ padding: '10px 16px 2px', margin: 0, fontSize: 11 }}>
                          {g.label}
                        </div>
                        {g.items.map((it) => (
                          <button key={it.key} type="button" onClick={() => goSearchItem(it)} className="fk-kb-search-row">
                            <span style={{ fontSize: 15, lineHeight: '20px', flexShrink: 0, color: 'var(--fk-orange)', display: 'flex', paddingTop: 1 }}>
                              {searchKindIcon(it.kind)}
                            </span>
                            <span style={{ minWidth: 0, flex: 1 }}>
                              <span style={{ display: 'block', fontSize: 14, fontWeight: 600, color: 'var(--fk-text)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                {it.title}
                              </span>
                              {it.sub && (
                                <span style={{ display: 'block', fontSize: 12, color: 'var(--fk-text-muted)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', marginTop: 2 }}>
                                  {it.sub}
                                </span>
                              )}
                            </span>
                          </button>
                        ))}
                      </div>
                    ))}
                    <button type="button" className="fk-kb-search-row" onClick={doSearch} style={{ borderTop: '1px solid var(--fk-border)', fontWeight: 600, color: 'var(--fk-orange)', fontSize: 13 }}>
                      <SearchOutlined /> Все результаты по «{debouncedQuery}»
                    </button>
                  </>
                )}
              </div>
            )}
          </div>
          {(popularQ.data ?? []).length > 0 && (
            <div className="fk-kb-popular">
              Часто ищут:
              {(popularQ.data ?? []).map((p) => (
                <Link key={p.query} to={`/search?q=${encodeURIComponent(p.query)}`}>
                  {p.query}
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Чипы типов */}
      <div className="fk-kb-chips">
        {TYPE_CHIPS.map((c) => (
          <Chip key={c.label} active={c.type === activeType} onClick={() => setActiveType(c.type)}>
            {c.label}
          </Chip>
        ))}
      </div>

      {/* Выбранный тип — список статей этого типа (серверный фильтр). */}
      {activeType && (
        <section className="fk-kb-panel fk-kb-panel-pad">
          <div className="fk-kb-rail-title">
            {materialIcon(activeType)}
            {TYPE_CHIPS.find((c) => c.type === activeType)?.label ?? MATERIAL_LABELS[activeType]}
            <span className="fk-kb-mono" style={{ marginLeft: 'auto' }}>
              {typeQuery.data ? typeItems.length : ''}
            </span>
          </div>
          {typeQuery.isLoading ? (
            <Skeleton active paragraph={{ rows: 3 }} />
          ) : typeItems.length === 0 ? (
            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Материалов такого типа пока нет" />
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', columnGap: 24 }}>
              {typeItems.map((a) => (
                <Link key={a.id} to={`/article/${a.slug}`} className="fk-kb-link-row">
                  <span className="fk-kb-link-title">{a.title}</span>
                  <span className="fk-kb-link-meta">{[sectionOf(a, blockNameByCode), kbShortDate(a.publishedAt)].filter(Boolean).join(' · ')}</span>
                </Link>
              ))}
            </div>
          )}
        </section>
      )}

      {/* Разделы */}
      {blocksQuery.error ? (
        <Empty description="Не удалось загрузить разделы" />
      ) : blocksQuery.isLoading ? (
        <Skeleton active paragraph={{ rows: 6 }} />
      ) : blocks.length === 0 && !canAdmin ? (
        <Empty description="Разделы ещё не созданы" />
      ) : (
        <div className="fk-kb-sections">
          {orderedBlocks.map((b, i) => (
            <SpaceCard
              key={b.id}
              block={b}
              dimmed={blocksWithType ? !blocksWithType.has(b.code) : false}
              canAdmin={canAdmin}
              isDragOver={overIndex === i && dragIndex !== null && dragIndex !== i}
              onEdit={() => setEditingBlock(b)}
              onAddArticle={() => {
                setNewArticleBlock(b.code);
                setNewArticleOpen(true);
              }}
              onDelete={() => deleteMutation.mutate(b.id)}
              onDragStart={() => setDragIndex(i)}
              onDragEnterCard={() => setOverIndex(i)}
              onDragEndCard={() => {
                setDragIndex(null);
                setOverIndex(null);
              }}
              onDropCard={() => {
                if (dragIndex !== null) handleDrop(dragIndex, i);
              }}
            />
          ))}
          {canAdmin && (
            <button type="button" className="fk-kb-add-card" onClick={() => setCreateOpen(true)}>
              <PlusOutlined /> Добавить раздел
            </button>
          )}
        </div>
      )}

      {/* Рейлы */}
      <div className="fk-kb-rails">
        {rails.map((r) => (
          <section key={r.key} className="fk-kb-panel fk-kb-panel-pad">
            <div className="fk-kb-rail-title">
              {r.icon}
              {r.title}
            </div>
            {r.loading ? (
              <Skeleton active paragraph={{ rows: 4 }} title={false} />
            ) : r.items.length === 0 ? (
              <div className="fk-kb-muted" style={{ fontSize: 13, padding: '6px 0' }}>
                {r.empty}
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                {r.items.map((a) => (
                  <Link key={a.key} to={a.to} className="fk-kb-link-row">
                    <span className="fk-kb-link-title">{a.title}</span>
                    {a.meta && <span className="fk-kb-link-meta">{a.meta}</span>}
                  </Link>
                ))}
              </div>
            )}
            {r.more && (
              <Link to={r.more.to} style={{ display: 'inline-block', marginTop: 10, fontSize: 13, fontWeight: 600 }}>
                {r.more.label} →
              </Link>
            )}
          </section>
        ))}
      </div>

      <div className="fk-kb-muted" style={{ fontSize: 13 }}>
        Не нашли нужную информацию?{' '}
        <Link to="/knowledge/ask" style={{ fontWeight: 600 }}>
          Задайте вопрос УК
        </Link>{' '}
        — ответим и пополним базу знаний.
      </div>

      <SectionModal
        open={createOpen}
        block={null}
        onClose={() => setCreateOpen(false)}
        onSubmit={(d) => createMutation.mutate(d)}
        saving={createMutation.isPending}
      />
      <SectionModal
        open={editingBlock !== null}
        block={editingBlock}
        onClose={() => setEditingBlock(null)}
        onSubmit={(d) => {
          if (editingBlock) updateMutation.mutate({ id: editingBlock.id, ...d });
        }}
        saving={updateMutation.isPending}
        onDelete={editingBlock ? () => deleteMutation.mutate(editingBlock.id) : undefined}
        deleting={deleteMutation.isPending}
      />
    </div>
  );
}

interface SpaceCardProps {
  block: KbBlock;
  dimmed: boolean;
  canAdmin: boolean;
  isDragOver: boolean;
  onEdit: () => void;
  onAddArticle: () => void;
  onDelete: () => void;
  onDragStart: () => void;
  onDragEnterCard: () => void;
  onDragEndCard: () => void;
  onDropCard: () => void;
}

/**
 * Карточка раздела (прототип: плитка-иконка цвета раздела, имя, число статей
 * моноширинным, описание). Админ: ⋮-меню (редактировать / добавить статью /
 * удалить пустой) и перестановка перетаскиванием ТОЛЬКО за ручку ⠿ — клик по
 * карточке остаётся переходом в раздел.
 */
function SpaceCard({
  block,
  dimmed,
  canAdmin,
  isDragOver,
  onEdit,
  onAddArticle,
  onDelete,
  onDragStart,
  onDragEnterCard,
  onDragEndCard,
  onDropCard,
}: SpaceCardProps): JSX.Element {
  const [dragArmed, setDragArmed] = useState(false);
  const isEmpty = block.articleCount === 0;

  const menuItems: MenuProps['items'] = [
    { key: 'edit', icon: <EditOutlined />, label: 'Редактировать' },
    { key: 'add', icon: <PlusOutlined />, label: 'Добавить статью' },
    { type: 'divider' },
    { key: 'delete', icon: <DeleteOutlined />, danger: true, disabled: !isEmpty, label: isEmpty ? 'Удалить раздел' : 'Удалить (есть статьи)' },
  ];
  const onMenuClick: MenuProps['onClick'] = ({ key, domEvent }) => {
    domEvent.preventDefault();
    domEvent.stopPropagation();
    if (key === 'edit') onEdit();
    else if (key === 'add') onAddArticle();
    else if (key === 'delete' && isEmpty) onDelete();
  };

  return (
    <Link
      to={`/block/${block.code}`}
      className="fk-kb-section"
      draggable={canAdmin && dragArmed}
      onDragStart={(e) => {
        if (!canAdmin || !dragArmed) {
          e.preventDefault();
          return;
        }
        e.dataTransfer.effectAllowed = 'move';
        onDragStart();
      }}
      onDragEnter={(e) => {
        if (!canAdmin) return;
        e.preventDefault();
        onDragEnterCard();
      }}
      onDragOver={(e) => {
        if (canAdmin) e.preventDefault();
      }}
      onDrop={(e) => {
        if (!canAdmin) return;
        e.preventDefault();
        onDropCard();
      }}
      onDragEnd={() => {
        setDragArmed(false);
        onDragEndCard();
      }}
      style={{
        opacity: dimmed ? 0.45 : 1,
        outline: isDragOver ? '2px dashed var(--fk-orange)' : 'none',
        outlineOffset: 2,
        paddingRight: canAdmin ? 44 : undefined,
      }}
    >
      <span className="fk-kb-section-icon" style={{ background: softColor(block.color), color: sectionAccent(block.color) }}>
        {renderSectionIcon(block.icon)}
      </span>
      <div style={{ minWidth: 0, flex: 1 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
          <span className="fk-kb-section-name">{block.name}</span>
          <span className="fk-kb-mono" title={`${block.articleCount} ${plural(block.articleCount, 'статья', 'статьи', 'статей')}`}>
            {block.articleCount}
          </span>
        </div>
        <div className="fk-kb-section-desc">{block.description?.trim() || SPACE_DESCRIPTIONS[block.code] || 'Статьи, инструкции и материалы раздела'}</div>
      </div>
      {canAdmin && (
        <span style={{ position: 'absolute', right: 10, top: 10, display: 'flex', flexDirection: 'column', gap: 4 }}>
          <Dropdown menu={{ items: menuItems, onClick: onMenuClick }} trigger={['click']} placement="bottomRight">
            <button
              type="button"
              aria-label="Действия с разделом"
              title="Действия с разделом"
              className="fk-kb-icon-btn"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
            >
              <MoreOutlined />
            </button>
          </Dropdown>
          <span
            aria-label="Перетащить для перестановки"
            title="Перетащить для перестановки"
            className="fk-kb-icon-btn"
            style={{ cursor: 'grab', borderColor: 'transparent' }}
            onMouseDown={() => setDragArmed(true)}
            onMouseUp={() => setDragArmed(false)}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
          >
            <HolderOutlined />
          </span>
        </span>
      )}
    </Link>
  );
}

const labelStyle: React.CSSProperties = { fontSize: 12.5, fontWeight: 600, color: 'var(--fk-text-2)', marginBottom: 6 };

/**
 * Универсальная модалка раздела: название + пикер иконок + цвет.
 * block=null → режим создания (createBlock).
 * block≠null → режим редактирования (предзаполнено; updateBlock) + кнопка
 *   «Удалить раздел» (Popconfirm). Для непустых блоков (articleCount>0) кнопку
 *   удаления прячем — бэк всё равно вернёт ошибку, но так честнее для UX.
 */
function SectionModal({
  open,
  block,
  onClose,
  onSubmit,
  saving,
  onDelete,
  deleting,
}: {
  open: boolean;
  block: WikiBlockDto | null;
  onClose: () => void;
  onSubmit: (d: { name: string; icon: string; color: string }) => void;
  saving: boolean;
  onDelete?: () => void;
  deleting?: boolean;
}): JSX.Element {
  const isEdit = block !== null;
  const [name, setName] = useState('');
  const [icon, setIcon] = useState<string>('book');
  const [color, setColor] = useState<string>(COLOR_CHOICES[0]);

  // Предзаполняем поля из блока при открытии в режиме редактирования.
  useEffect(() => {
    if (open && block) {
      setName(block.name);
      // Если icon — известный ключ, берём его; иначе фолбэк 'book' (эмодзи/пусто).
      setIcon(block.icon && SECTION_ICONS[block.icon] ? block.icon : 'book');
      setColor(/^#?[0-9a-f]{6}$/i.test(block.color || '') ? block.color : COLOR_CHOICES[0]);
    }
  }, [open, block]);

  const submit = (): void => {
    const n = name.trim();
    if (!n) {
      message.warning('Введите название раздела');
      return;
    }
    onSubmit({ name: n, icon, color });
  };

  // Можно ли удалять: только в режиме edit, для пустого блока.
  const canDelete = isEdit && !!onDelete && block.articleCount === 0;

  return (
    <Modal
      open={open}
      onCancel={onClose}
      title={isEdit ? 'Редактировать раздел' : 'Создать раздел'}
      okText={isEdit ? 'Сохранить' : 'Создать'}
      cancelText="Отмена"
      confirmLoading={saving}
      onOk={submit}
      destroyOnHidden
      // В режиме редактирования слева добавляем кнопку удаления (Popconfirm).
      footer={(originNode) => (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
          <div>
            {canDelete && (
              <Popconfirm
                title="Удалить раздел?"
                description="Действие необратимо."
                okText="Удалить"
                cancelText="Отмена"
                okButtonProps={{ danger: true, loading: deleting }}
                onConfirm={onDelete}
              >
                <button
                  type="button"
                  className="fk-btn"
                  style={{ color: '#dc2626', border: '1px solid #f3c2c2', background: '#fff', display: 'inline-flex', alignItems: 'center', gap: 6 }}
                >
                  <DeleteOutlined /> Удалить раздел
                </button>
              </Popconfirm>
            )}
            {isEdit && !canDelete && (
              <span style={{ fontSize: 12, color: 'var(--fk-text-muted)' }}>
                В разделе есть статьи — удаление недоступно
              </span>
            )}
          </div>
          <div style={{ display: 'flex', gap: 8 }}>{originNode}</div>
        </div>
      )}
      afterClose={() => {
        setName('');
        setIcon('book');
        setColor(COLOR_CHOICES[0]);
      }}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16, marginTop: 8 }}>
        <div>
          <div style={labelStyle}>Название раздела</div>
          <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Например: Сезонное меню" maxLength={60} onPressEnter={submit} autoFocus />
        </div>
        <div>
          <div style={labelStyle}>Иконка</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {ICON_CHOICES.map((key) => {
              const Ic = SECTION_ICONS[key];
              const active = key === icon;
              return (
                <button
                  key={key}
                  type="button"
                  onClick={() => setIcon(key)}
                  style={{
                    width: 40, height: 40, borderRadius: 6, display: 'grid', placeItems: 'center', fontSize: 18, cursor: 'pointer',
                    border: '1.5px solid ' + (active ? color : 'var(--fk-border)'),
                    background: active ? softColor(color) : 'var(--fk-surface)',
                    color: active ? color : 'var(--fk-text-2)',
                  }}
                >
                  <Ic />
                </button>
              );
            })}
          </div>
        </div>
        <div>
          <div style={labelStyle}>Цвет</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {COLOR_CHOICES.map((c) => (
              <button
                key={c}
                type="button"
                aria-label={c}
                onClick={() => setColor(c)}
                style={{
                  width: 28, height: 28, borderRadius: '50%', background: c, cursor: 'pointer',
                  border: '2px solid ' + (c === color ? 'var(--fk-text)' : 'transparent'),
                  boxShadow: c === color ? '0 0 0 2px #fff inset' : 'none',
                }}
              />
            ))}
          </div>
        </div>
      </div>
    </Modal>
  );
}
